import { getReasonTextFromIdAndLang } from "@/lib/reasons/reason-text-from-id-and-lang";
import { getCurrentLocale } from "@/lib/translator";
import useShopifyShopLocations from "@babackapp/private-api-client/client/use-shopify-shop-locations";
import type { ShopDto } from "@babackapp/private-api-client/types";
import FormOptionsSelector from "@babackapp/react-hook-forms-components/form-option-selector";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import { Trash2 } from "lucide-react";
import {
  ROUTING_CONDITION_KEYS,
  ROUTING_CONDITION_LABEL_KEYS,
  type RoutingCondition,
  type RoutingConditionKey,
} from "../lib/routing-location-rules";

interface RoutingConditionRowProps {
  shop: ShopDto;
  condition: RoutingCondition;
  onChange: (condition: RoutingCondition) => void;
  onRemove: () => void;
  canRemove: boolean;
}

export function RoutingConditionRow({
  shop,
  condition,
  onChange,
  onRemove,
  canRemove,
}: RoutingConditionRowProps) {
  const t = useTranslation();
  const { data: shopifyLocations } = useShopifyShopLocations(shop.id);

  const activeReasons = shop.reasons.filter((reason) => reason.active);

  // Changer de critère invalide la valeur saisie : un id de raison n'a aucun
  // sens comme code pays.
  const handleKeyChange = (key: string | number) =>
    onChange({ key: String(key) as RoutingConditionKey, value: "" });

  const handleValueChange = (value: string | number) =>
    onChange({ ...condition, value: String(value) });

  return (
    <div className="flex items-start gap-2">
      <div className="flex-1 space-y-2">
        <FormOptionsSelector
          label={""}
          value={condition.key}
          onChange={handleKeyChange}
          options={ROUTING_CONDITION_KEYS.map((key) => ({
            key,
            label: t(ROUTING_CONDITION_LABEL_KEYS[key]),
          }))}
        />

        {condition.key === "countryCode" && (
          <Input
            type="text"
            value={condition.value}
            onChange={(event) =>
              handleValueChange(event.target.value.toUpperCase())
            }
          />
        )}

        {condition.key === "orderTag" && (
          <Input
            type="text"
            value={condition.value}
            onChange={(event) => handleValueChange(event.target.value)}
          />
        )}

        {condition.key === "reasonId" && (
          <FormOptionsSelector
            label={""}
            value={condition.value}
            onChange={handleValueChange}
            options={activeReasons.map((reason) => ({
              key: reason.id ?? "",
              label:
                getReasonTextFromIdAndLang(
                  reason.id ?? "",
                  getCurrentLocale()?.code ?? "",
                  shop,
                ) ?? "",
            }))}
          />
        )}

        {condition.key === "fulfillmentOriginLocationId" && (
          <FormOptionsSelector
            label={""}
            value={condition.value}
            onChange={handleValueChange}
            options={(shopifyLocations ?? []).map((location) => ({
              key: location.id,
              label: location.name,
            }))}
          />
        )}
      </div>

      {canRemove && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onRemove}
          title={t("settings.shipping.rules.removeCondition")}
          className="text-red-600 hover:text-red-800 hover:bg-red-50"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
